import {
    ActionReducerMapBuilder,
    Draft,
    SerializedError,
    createAsyncThunk,
    createSlice,
} from '@reduxjs/toolkit';
import { IAction, SliceProps } from '../../store';
import { urlAdvancedExportConfig } from '../../../Constants';
import { IExportAdditionalDocument } from '../../../components/General/Dialogs/_parts/AdvancedExport';
import { apiFetch } from '../../../util/ProjectUtils';

export enum ExportConfigEnum {
    ExportAdditionalDocument = 'ExportAdditionalDocument',
    ExportDatamodelOriginal = 'ExportDatamodelOriginal',
    ExportExcelReport = 'ExportExcelReport',
    ExportFileOriginal = 'ExportFileOriginal',
    ExportFileRedliningPdf = 'ExportFileRedliningPdf',
    ExportMailReport = 'ExportMailReport',
    ExportNote = 'ExportNote',
    ExportProtocol = 'ExportProtocol',
    ExportRedliningReport = 'ExportRedliningReport',
    FormatFileName = 'FormatFileName',
}

export type IncludeInExportType =
    | ExportConfigEnum.ExportDatamodelOriginal
    | ExportConfigEnum.ExportFileOriginal
    | ExportConfigEnum.ExportNote
    | ExportConfigEnum.ExportProtocol;

export const getExportConfig = ({
    additionalFiles,
    includeInExport,
    documentTypeName,
    protocolOnly,
}: {
    additionalFiles: IExportAdditionalDocument[];
    includeInExport: string[];
    documentTypeName: string;
    protocolOnly?: boolean;
}): IAdvancedExportConfig => ({
    ExportAdditionalDocument: additionalFiles,
    ExportDatamodelOriginal: includeInExport.includes(ExportConfigEnum.ExportDatamodelOriginal)
        ? '1'
        : '0',
    ExportExcelReport: '',
    ExportFileOriginal: includeInExport.includes(ExportConfigEnum.ExportFileOriginal) ? '1' : '0',
    ExportFileRedliningPdf: protocolOnly ? '0' : '1',
    ExportMailReport: [],
    ExportNote: includeInExport.includes(ExportConfigEnum.ExportNote) ? '1' : '0',
    ExportProtocol: includeInExport.includes(ExportConfigEnum.ExportProtocol) ? '1' : '0',
    ExportRedliningReport: [],
    FormatFileName: documentTypeName,
});

const includeInExportOptions: IIncludeInExportOption[] = [
    {
        translationKey: 'pdfNotes',
        id: ExportConfigEnum.ExportNote,
    },
    {
        translationKey: 'originalFileAttachments',
        id: ExportConfigEnum.ExportFileOriginal,
    },
    {
        translationKey: 'originalFileDataModel',
        id: ExportConfigEnum.ExportDatamodelOriginal,
    },
    {
        translationKey: 'protocol',
        id: ExportConfigEnum.ExportProtocol,
    },
];

/**
 * Initial slice state value.
 */
const initialState: IProjectAdvancedExportDocumentsState = {
    formatFileNameOptions: null,
    includeInExportOptions: includeInExportOptions,
    exportAdditionalDocumentOptions: null,
    loading: false,
    error: null,
};

const reducers = {};
/**
 * Collection of the thunks for this slice, along with their reducer functions.
 */
const thunks = {
    /**
     * Get data for Advanced Export Options
     */
    getAdvancedExportConfig: async (config: { replicaId: string }) => {
        try {
            const { replicaId } = config;
            const request: RequestInit = {
                headers: {
                    replicaId,
                },
            };

            const response = await apiFetch(urlAdvancedExportConfig, request);

            return response.ok
                ? Promise.resolve(await response.json())
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },
    /**
     * Registers the 'extraReducers' functions for the slice's thunks.
     */
    _reducers: (builder: ActionReducerMapBuilder<IProjectAdvancedExportDocumentsState>) => {
        /**
         * Reducers for the 'getAdvancedExportConfig' thunk.
         */
        builder.addCase(
            getAdvancedExportConfig.pending,
            (state: Draft<IProjectAdvancedExportDocumentsState>) => {
                state.loading = true;
                state.error = null;
            },
        );

        builder.addCase(
            getAdvancedExportConfig.fulfilled,
            (
                state: Draft<IProjectAdvancedExportDocumentsState>,
                action: IAction<IAdvancedExportConfigResponse>,
            ) => {
                state.exportAdditionalDocumentOptions = action.payload.ExportAdditionalDocument;
                state.formatFileNameOptions = action.payload.FormatFileName;

                state.loading = false;
                state.error = null;
            },
        );

        builder.addCase(
            getAdvancedExportConfig.rejected,
            (
                state: Draft<IProjectAdvancedExportDocumentsState>,
                action: IAction<SerializedError>,
            ) => {
                state.loading = false;
                state.error = action.error;
            },
        );
    },
};

export const getAdvancedExportConfig = createAsyncThunk(
    `${urlAdvancedExportConfig}`,
    thunks.getAdvancedExportConfig,
);

export interface IFormatFilleNameEntry {
    display: string;
    key: string;
}

export interface IAdvancedExportConfigResponse {
    ExportAdditionalDocument: IDocTypeVariant[];
    FormatFileName: IFormatFilleNameEntry[];
}

export interface IIncludeInExportOption {
    translationKey: string;
    id: IncludeInExportType;
}

export interface IDocTypeVariant {
    docType: string;
    docTypeName: string;
    docVariants: string[];
}

export interface IAdvancedExportConfig {
    [ExportConfigEnum.ExportAdditionalDocument]: IExportAdditionalDocument[];
    [ExportConfigEnum.ExportDatamodelOriginal]: string;
    [ExportConfigEnum.ExportExcelReport]: string;
    [ExportConfigEnum.ExportFileOriginal]: string;
    [ExportConfigEnum.ExportFileRedliningPdf]: string;
    [ExportConfigEnum.ExportMailReport]: string[];
    [ExportConfigEnum.ExportNote]: string;
    [ExportConfigEnum.ExportProtocol]: string;
    [ExportConfigEnum.ExportRedliningReport]: string[];
    [ExportConfigEnum.FormatFileName]: string;
}
/**
 * Interface that describes the state object for this redux slice.
 */
export interface IProjectAdvancedExportDocumentsState extends SliceProps {
    exportAdditionalDocumentOptions: IDocTypeVariant[];
    formatFileNameOptions: IFormatFilleNameEntry[];
    includeInExportOptions: IIncludeInExportOption[];
}

/**
 * Create the redux slice.
 */
export const projectAdvancedExportDocumentsDataSlice = createSlice({
    name: 'projectAdvancedExportData',
    extraReducers: thunks._reducers,
    initialState,
    reducers,
});

/**
 * Export slice reducer as default.
 */
export default projectAdvancedExportDocumentsDataSlice.reducer;
